import type { AudienceAuthorIdentityKind, AudienceCategory, RoomThread } from "../../shared/types.ts";
import {
  compareThreads,
  type SortableThread,
  type ThreadSort,
} from "../../shared/threadSorting.ts";

export type { ThreadSort } from "../../shared/threadSorting.ts";

export type AuthorKind = "participant" | "ai" | "anonymous" | "hidden";
export interface AuthorDisplay { kind: AuthorKind; label: string }

export function authorDisplay(
  thread: Pick<RoomThread, "threadOrigin" | "authorLabel" | "authorIdentityKind">,
): AuthorDisplay {
  if (thread.authorIdentityKind === "hidden") return { kind: "hidden", label: "" };
  if (thread.authorIdentityKind === "pseudonym") {
    return { kind: "anonymous", label: thread.authorLabel ?? "" };
  }
  if (thread.threadOrigin === "ai") return { kind: "ai", label: "Comodo" };
  return { kind: "participant", label: thread.authorLabel ?? "" };
}

export function replyAuthorDisplay(
  authorLabel: string | null,
  authorIdentityKind?: AudienceAuthorIdentityKind,
): AuthorDisplay {
  if (authorIdentityKind === "hidden") return { kind: "hidden", label: "" };
  if (authorIdentityKind === "pseudonym") return { kind: "anonymous", label: authorLabel ?? "" };
  return { kind: "participant", label: authorLabel ?? "" };
}

export function sortThreads(threads: RoomThread[], sort: ThreadSort): RoomThread[] {
  return [...threads].sort((a, b) => compareThreads(sortableThread(a), sortableThread(b), sort));
}

/** A thread's audience approval is the post's likes plus its replies' likes —
 *  a heavily-liked answer should surface its thread even when the post itself
 *  collected few upvotes. Counts the loaded replies (staff/participant states
 *  load them capped at REPLIES_PER_POST_LIMIT, far above real reply volumes). */
export function totalUpvotes(thread: RoomThread): number {
  return thread.threadLikeCount
    ?? thread.likeCount + (thread.replies ?? []).reduce((sum, reply) => sum + reply.likeCount, 0);
}

export function sortableThread(thread: RoomThread): SortableThread {
  return {
    id: thread.id,
    createdAt: thread.createdAt,
    lastActivityAt: thread.lastActivityAt,
    replyCount: thread.replyCount,
    threadLikeCount: totalUpvotes(thread),
  };
}

/** Shared status buckets: queued needs approval, shown stays live, and answered
 *  renders in its own labelled section. Hidden remains recoverable; archived is excluded. */
export function splitSections(threads: RoomThread[]) {
  const needsApproval = threads.filter((t) => t.status === "queued");
  const live = threads.filter((t) => t.status === "shown");
  const answered = threads.filter((t) => t.status === "answered");
  const hidden = threads.filter((t) => t.status === "hidden");
  return { needsApproval, live, answered, board: [...live, ...answered], hidden };
}

export interface CategoryGroup { category: AudienceCategory | null; threads: RoomThread[] }

export function groupByCategory(threads: RoomThread[], categories: AudienceCategory[]): CategoryGroup[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const groups = new Map<string, CategoryGroup>();
  for (const t of threads) {
    const key = t.categoryId && byId.has(t.categoryId) ? t.categoryId : "__none__";
    const existing = groups.get(key);
    if (existing) {
      existing.threads.push(t);
    } else {
      groups.set(key, { category: key === "__none__" ? null : byId.get(key)!, threads: [t] });
    }
  }
  return [...groups.values()].sort((a, b) =>
    (a.category?.label ?? "￿").localeCompare(b.category?.label ?? "￿"),
  );
}

export function categoryLabel(thread: RoomThread, categories: AudienceCategory[]): string | undefined {
  return categories.find((c) => c.id === thread.categoryId)?.label;
}

export function isOnAir(
  thread: Pick<RoomThread, "id">,
  projector: { mode: string; projectedThreadId?: string } | null | undefined,
): boolean {
  return projector?.mode === "thread" && projector?.projectedThreadId === thread.id;
}

export function fmtAgo(ts: number, now = Date.now()): string {
  const m = Math.max(0, Math.floor((now - ts) / 60000));
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${Math.floor(m / 1440)}d`;
}
